import React, { ReactNode, useState } from "react";
import { DailyIcon } from "@/features/daily/components/DailyIcon";
import type { QueueRowVm, QueueSortKey } from "@/features/daily/components/diagnostic/diagnostic-view-model";

/** D106: a whole day can hold thousands of rows; they are drawn in chunks, never all at once. */
export const QUEUE_TABLE_CHUNK = 200;

type Props = {
    /** D131: the table's title and count share one line with the sort control. */
    heading: ReactNode;
    caption: string;
    /** The accessible name of the scrollable table region. */
    regionLabel: string;
    rows: QueueRowVm[];
    emptyMessage: string;
    sortKey: QueueSortKey;
    /** D138: the day and an email's history come newest first from the server. */
    newestFirst: boolean;
    onSortChange: (sortKey: QueueSortKey) => void;
    onOpen: (operationId: string, trigger: HTMLElement) => void;
    onCopy: (value: string, what: string) => void;
    onFilterEmail: (email: string) => void;
};

/**
 * FR-008/FR-021/FR-025. A plain data table: column-scoped headers, one control per
 * row that both opens the evidence and is the element focus returns to when it
 * closes, and the account email beside each operation (D99) — copyable, and a
 * filter in one click. The email never reaches the URL: filtering sends it as the
 * search header, like the search box does.
 *
 * Sorting re-orders THIS page and nothing else (D32). The server paginates oldest
 * first; asking for "priority on this page" never asks the server for a different
 * page, and amounts are deliberately not sortable because Daily has no FX.
 */
export const QueueTable: React.FC<Props> = ({
    heading, caption, regionLabel, rows, emptyMessage, sortKey, newestFirst, onSortChange, onOpen, onCopy, onFilterEmail,
}) => {
    const [visible, setVisible] = useState(QUEUE_TABLE_CHUNK);
    const shown = rows.slice(0, visible);
    const remaining = rows.length - shown.length;
    return (
    <>
        <div className="daily-panel__head">
            {heading}
            <div className="daily-actions" role="group" aria-label="Orden de esta página">
                <span className="daily-small daily-muted">Orden:</span>
                <button
                    type="button"
                    className="daily-button"
                    aria-pressed={sortKey === "server"}
                    onClick={() => onSortChange("server")}
                >
                    {newestFirst ? "Más recientes primero" : "Más antiguas primero"}
                </button>
                {newestFirst ? (
                    <button
                        type="button"
                        className="daily-button"
                        aria-pressed={sortKey === "oldest"}
                        onClick={() => onSortChange("oldest")}
                    >
                        Más antiguas primero
                    </button>
                ) : null}
                <button
                    type="button"
                    className="daily-button"
                    aria-pressed={sortKey === "impact"}
                    onClick={() => onSortChange("impact")}
                >
                    Prioridad en esta página
                </button>
            </div>
        </div>
        {/* D128: the table scrolls inside its own keyboard-reachable region so the page fits the screen. */}
        <div className="daily-table__scroll daily-queue-scroll" role="region" aria-label={regionLabel} tabIndex={0}>
            <table className="daily-table daily-queue-table">
                <caption className="daily-visually-hidden">{caption}</caption>
                <thead className="daily-table__head--sticky">
                    <tr>
                        <th scope="col" aria-sort={sortKey === "impact" ? "none" : newestFirst && sortKey === "server" ? "descending" : "ascending"}>Creada</th>
                        <th scope="col">ID de operación</th>
                        <th scope="col">Correo</th>
                        <th scope="col">Estado</th>
                        <th scope="col">Par</th>
                        <th scope="col" className="is-numeric">Recibido / proyectado</th>
                        <th scope="col" className="is-numeric">Tiempo</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={7} className="daily-table__empty">{emptyMessage}</td>
                        </tr>
                    ) : shown.map((row) => (
                        <tr
                            key={row.operationId}
                            data-tone={row.stateTone}
                            className="daily-row-clickable"
                            // D120: the whole row opens the detail with the mouse; from the
                            // keyboard, the id button is the one activating control (no
                            // interactive element is nested inside another).
                            onClick={(event) => {
                                const trigger = event.currentTarget.querySelector<HTMLElement>("[data-row-open]");
                                onOpen(row.operationId, trigger ?? event.currentTarget);
                            }}
                        >
                            <td className="num">
                                {row.createdLabel}
                                {row.createdBeforeLabel === null ? null : (
                                    <small className="daily-created-before" title={row.createdFullLabel}>{row.createdBeforeLabel}</small>
                                )}
                            </td>
                            <td>
                                {/* The id opens the evidence and is where focus returns when it closes. */}
                                <button
                                    type="button"
                                    className="daily-link daily-mono"
                                    data-row-open=""
                                    aria-label={`Ver detalle de ${row.operationId}`}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onOpen(row.operationId, event.currentTarget);
                                    }}
                                >
                                    {row.operationId}
                                </button>
                                <button
                                    type="button"
                                    className="daily-filter-email"
                                    aria-label={`Copiar operación ${row.operationId}`}
                                    title="Copiar ID"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onCopy(row.operationId, "ID de operación");
                                    }}
                                >
                                    <DailyIcon name="content_copy" />
                                </button>
                            </td>
                            <td>
                                {row.accountEmail === null ? (
                                    <span className="daily-muted">Sin correo registrado</span>
                                ) : (
                                    <>
                                        <span>{row.accountEmail}</span>
                                        <button
                                            type="button"
                                            className="daily-filter-email"
                                            aria-label={`Copiar correo ${row.accountEmail}`}
                                            title="Copiar correo"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                if (row.accountEmail !== null) onCopy(row.accountEmail, "Correo");
                                            }}
                                        >
                                            <DailyIcon name="content_copy" />
                                        </button>
                                        <button
                                            type="button"
                                            className="daily-filter-email"
                                            aria-label={`Filtrar por ${row.accountEmail}`}
                                            title={`Filtrar por ${row.accountEmail}`}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                if (row.accountEmail !== null) onFilterEmail(row.accountEmail);
                                            }}
                                        >
                                            <DailyIcon name="filter_alt" />
                                        </button>
                                    </>
                                )}
                            </td>
                            <td>
                                {/* The status is spelled out; the colour only repeats it (WCAG 1.4.1). */}
                                <span className="daily-pill" data-tone={row.statusTone}>{row.statusLabel}</span>
                                {row.stuck || row.pastDeadline || row.applicabilityNote !== null ? (
                                    <span className="daily-small daily-muted daily-queue-table__note">
                                        {row.stateLabel}
                                        {row.applicabilityNote === null ? null : ` · ${row.applicabilityNote}`}
                                    </span>
                                ) : null}
                            </td>
                            <td>{row.pairLabel}</td>
                            <td className="is-numeric">
                                <strong>{row.receivedLabel}</strong>
                                <span className="daily-muted"> / {row.projectedLabel}</span>
                            </td>
                            <td className="is-numeric">{row.durationLabel}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {remaining <= 0 ? null : (
                <button
                    type="button"
                    className="daily-button daily-queue-more"
                    onClick={() => setVisible((current) => current + QUEUE_TABLE_CHUNK)}
                >
                    Mostrar {Math.min(QUEUE_TABLE_CHUNK, remaining)} más · quedan {remaining}
                </button>
            )}
        </div>
    </>
    );
};
